import React, { useState, useEffect } from 'react';
import { Heart, Play, Eye, Loader, AlertCircle } from 'lucide-react';
import { getDiscoverCategories, getDiscoverVideos, getMediaUrl } from '../services/api';
import VideoModal from './VideoModal';

const DiscoverPage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [categories, setCategories] = useState([]);
  const tabs = [{ id: 'all', name: '全部' }, ...categories.map(category => ({
    id: category._id,
    name: category.name
  }))];

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getDiscoverCategories();
        const nextCategories = Array.isArray(data) ? data : [];
        setCategories(nextCategories);
        setActiveTab((currentTab) => currentTab !== 'all' && !nextCategories.some(category => category._id === currentTab)
          ? 'all'
          : currentTab);
      } catch (err) {
        console.error('获取发现页分类失败:', err);
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  // 获取发现页视频数据
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // 调用API获取视频数据
        const params = activeTab === 'all' ? {} : { category: activeTab };
        const data = await getDiscoverVideos(params);

        console.log('📊 获取到的发现页视频数据:', data);

        // 处理视频数据
        if (data && data.videos && data.videos.length > 0) {
          setVideos(data.videos.map(video => ({
            id: video._id || video.id,
            title: video.title,
            cover: getMediaUrl(video.coverUrl || video.cover),
            videoUrl: getMediaUrl(video.videoUrl),
            views: video.views || '0',
            likes: video.likes || '0',
            duration: video.duration,
            category: video.category,
            author: video.author
          })));
        } else if (data && Array.isArray(data)) {
          // 如果直接返回数组
          setVideos(data.map(video => ({
            id: video._id || video.id,
            title: video.title,
            cover: getMediaUrl(video.coverUrl || video.cover),
            videoUrl: getMediaUrl(video.videoUrl),
            views: video.views || '0',
            likes: video.likes || '0',
            duration: video.duration,
            category: video.category,
            author: video.author
          })));
        } else {
          setVideos([]);
        }
      } catch (err) {
        console.error('❌ 获取发现页视频失败:', err);
        setError(err.message);
        setVideos([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVideos();
  }, [activeTab]); // 当切换标签时重新获取数据

  const activeCategoryName = categories.find(category => category._id === activeTab)?.name;
  const filteredVideos = activeTab === 'all'
    ? videos
    : videos.filter(video => video.category === activeCategoryName);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Loading状态 */}
      {isLoading && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600 text-sm">正在加载视频...</p>
          </div>
        </div>
      )}

      {/* Error状态 */}
      {error && !isLoading && videos.length === 0 && (
        <div className="flex items-center justify-center min-h-screen p-6">
          <div className="text-center max-w-md">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-800 font-medium mb-2">加载失败</p>
            <p className="text-gray-600 text-sm mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              重新加载
            </button>
          </div>
        </div>
      )}

      {/* 内容区域（加载完成后显示） */}
      {!isLoading && !error && (
        <>
          {/* Header - Sticky at top including nav space */}
          <div className="sticky top-0 bg-gray-50 z-10 pt-20">
        <div className="px-4 py-4">
          
          {/* Tabs */}
          <div className="flex gap-6 overflow-x-auto scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative pb-3 whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'text-black text-lg font-medium'
                    : 'text-gray-500 text-base hover:text-gray-700'
                }`}
              >
                {tab.name}
                {/* Underline */}
                <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-black transition-all duration-300 ${
                  activeTab === tab.id ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
                }`} />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Video Grid - Masonry Layout */}
      <div className="px-4 py-4">
        {filteredVideos.length === 0 ? (
          <div className="flex min-h-[40vh] items-center justify-center text-center">
            <div>
              <p className="text-gray-800 font-medium">暂无视频</p>
              <p className="mt-2 text-sm text-gray-500">请先在 CMS 中发布该分类的内容</p>
            </div>
          </div>
        ) : (
        <div className="columns-2 gap-3">
          {filteredVideos.map((video) => (
            <div key={video.id} className="bg-white rounded-xl overflow-hidden shadow-sm mb-3 break-inside-avoid">
              <div className="relative">
                <img
                  src={video.cover}
                  alt={video.title}
                  className="w-full aspect-[3/4] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />
                
                {/* Play Button */}
                <button
                  type="button"
                  onClick={() => video.videoUrl && setPlayingVideo(video)}
                  className="absolute inset-0 flex items-center justify-center group"
                  aria-label={`播放 ${video.title}`}
                >
                  <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 opacity-0 group-hover:opacity-100 transition-all">
                    <Play className="w-6 h-6 text-white fill-white" />
                  </div>
                </button>

                {video.duration && (
                  <span className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {video.duration}
                  </span>
                )}


              </div>

              <div className="p-3">
                <h3 className="text-sm font-medium line-clamp-2 text-left mb-2">{video.title}</h3>
                {video.author && (
                  <p className="text-xs text-gray-400 text-left mb-2 line-clamp-1">{video.author}</p>
                )}
                {/* Stats */}
                <div className="flex items-center gap-3 text-gray-500 text-xs">
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    {video.views}
                  </span>
                  <span className="flex items-center gap-1">
                    <Heart className="w-3 h-3" />
                    {video.likes}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}

      </div>
        </>
      )}

      <VideoModal video={playingVideo} onClose={() => setPlayingVideo(null)} />

    </div>
  );
};

export default DiscoverPage;
